import { useState } from "react";

import { trackEvent } from "@lib/infrastructure";
import type { UsageStats } from "@lib/logic";
import { type Floor, getFloorsByBuildingId, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { Icon, type IconNames } from "@atoms";
import { cn } from "@utils";

import { useChangeFloor } from "../../shared/useChangeFloor";
import { HaNContext } from "../HaNContext";
import { getFloorListOffset, getFloorPageCount, getFloorPageIndex } from "./floorPages";
import { useCloseOnOutsideAction } from "./useCloseOnOutsideAction";

// In the map's bottom-right corner; on phones it sits above the carousel
export const HaNFloorSelector = () => {
  const { tab, building, floor, usageStatsPerFloor } = useRequiredContext(HaNContext);
  const changeFloor = useChangeFloor();
  const floors = useAppSelector(state => getFloorsByBuildingId(state, building.buildingId ?? ""));
  const [isOpen, setIsOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const floorsHighToLow = floors.toSorted((left, right) => right.number - left.number);
  const pageCount = getFloorPageCount(floors.length);
  const showsPaging = pageCount > 1;
  const hasUsageStats = [...usageStatsPerFloor.values()].some(stats => stats !== null);
  const selectedFloor = floors.find(f => f.id === floor.floorId);

  const trackingProps = { buildingId: building.buildingId ?? "", tab, currentFloorId: floor.floorId ?? "" };

  const open = () => {
    setPageIndex(
      getFloorPageIndex(
        floorsHighToLow.map(f => f.id),
        floor.floorId,
      ),
    );
    setIsOpen(true);

    trackEvent("HereAndNow_FloorSelector__Open", { ...trackingProps, hasUsageStats });
  };

  const close = () => {
    setIsOpen(false);

    trackEvent("HereAndNow_FloorSelector__Close", trackingProps);
  };

  const ref = useCloseOnOutsideAction<HTMLDivElement>({ enabled: isOpen, onOutsideAction: close });

  const selectFloor = (nextFloor: Floor) => {
    changeFloor(nextFloor.id, () => {
      trackEvent("HereAndNow_FloorSelector__Select", {
        ...trackingProps,
        hasUsageStats,
        floorId: nextFloor.id,
        floorNumber: nextFloor.number,
      });

      close();
    });
  };

  const showHigherFloors = () => {
    setPageIndex(Math.min(pageCount - 1, pageIndex + 1));

    trackEvent("HereAndNow_FloorSelector__PreviousPage", { ...trackingProps, pageCount, floorCount: floors.length });
  };

  const showLowerFloors = () => {
    setPageIndex(Math.max(0, pageIndex - 1));

    trackEvent("HereAndNow_FloorSelector__NextPage", { ...trackingProps, pageCount, floorCount: floors.length });
  };

  return (
    <div
      ref={ref}
      className="absolute bottom-5 right-5 z-10 flex items-end gap-2 max-sm:bottom-[148px]">
      {/* Shown at once when opening, and only hidden once the fade-out has finished when closing */}
      <div
        className={cn("overflow-clip rounded-lg shadow-lg [transition:opacity_200ms,transform_200ms]", {
          "invisible translate-y-2.5 opacity-0 [transition:opacity_200ms,transform_200ms,visibility_0s_200ms]": !isOpen,
        })}>
        {showsPaging && (
          <PageButton
            icon="caretUp"
            disabled={pageIndex >= pageCount - 1}
            onClick={showHigherFloors}
          />
        )}
        <div className="flex max-h-[480px] overflow-hidden">
          <div
            className="flex items-end transition-transform duration-300 ease-in-out"
            style={{ transform: `translateY(${getFloorListOffset(pageIndex, floors.length) * 100}%)` }}>
            <div className={cn("grid grid-cols-1", { "grid-cols-[auto_auto_auto]": hasUsageStats })}>
              {floorsHighToLow.map(f => (
                <FloorItem
                  key={f.id}
                  floor={f}
                  usageStats={usageStatsPerFloor.get(f.id) ?? null}
                  isSelected={f.id === floor.floorId}
                  hasPaging={showsPaging}
                  onSelect={selectFloor}
                />
              ))}
            </div>
          </div>
        </div>
        {showsPaging && (
          <PageButton
            className="border-t"
            icon="caretDown"
            disabled={pageIndex <= 0}
            onClick={showLowerFloors}
          />
        )}
      </div>

      <button
        type="button"
        aria-expanded={isOpen}
        className={cn(
          "relative -m-0.5 flex items-center justify-center gap-2 rounded-lg border-2 border-transparent bg-white px-4 py-2 shadow-lg transition-all hover:bg-gray-50 max-sm:border-mapiq-black-800* md:m-0 md:h-12 md:border-none",
          { "border-mapiq-black-800*": isOpen },
        )}
        onClick={isOpen ? close : open}>
        {selectedFloor && <span className="text-[15px] font-semibold leading-5">{selectedFloor.number}</span>}
        <Icon
          icon="floor"
          color="currentColor"
          className="size-4 md:size-5"
        />
      </button>
    </div>
  );
};

const PageButton = ({
  icon,
  disabled,
  onClick,
  className,
}: {
  icon: IconNames;
  disabled: boolean;
  onClick: () => void;
  className?: string;
}) => {
  return (
    <button
      type="button"
      className={cn(
        "z-10 flex h-12 w-full items-center justify-center gap-2 border-b bg-white px-5 py-4 transition-colors",
        { "text-mapiq-black-200": disabled },
        className,
      )}
      disabled={disabled}
      onClick={onClick}>
      <Icon
        icon={icon}
        color="currentColor"
        className="size-5"
      />
    </button>
  );
};

const FloorItem = ({
  floor,
  usageStats,
  isSelected,
  hasPaging,
  onSelect,
}: {
  floor: Floor;
  usageStats: UsageStats | null;
  isSelected: boolean;
  hasPaging: boolean;
  onSelect: (floor: Floor) => void;
}) => {
  return (
    <button
      type="button"
      className={cn(
        "relative col-span-full grid h-12 grid-cols-subgrid items-center gap-2 rounded-none border-b bg-white px-5 py-2 hover:bg-mapiq-black-50",
        "before:absolute before:inset-0 before:right-auto before:w-[4px] before:origin-left before:-translate-x-full before:transition-transform",
        {
          "bg-mapiq-black-50 before:translate-x-0 before:bg-mapiq-black-800*": isSelected,
          "last:border-b-0": hasPaging,
        },
      )}
      onClick={() => onSelect(floor)}>
      <span className="translate-y-[10%] text-center text-[15px] font-semibold leading-5">{floor.number}</span>
      {usageStats && (
        <>
          <div className={cn("w-10 rounded-sm", { "bg-mapiq-black-100": usageStats.ratio === 0 })}>
            <span
              className="block h-1.5 origin-left rounded-sm bg-functional-green transition-[width]"
              style={{ width: `${usageStats.ratio * 100}%` }}
            />
          </div>
          <small className="text-right text-mapiq-black-500">{`${usageStats.available}/${usageStats.total}`}</small>
        </>
      )}
    </button>
  );
};
